// src/services/CustomersService.js
import axios from 'axios'
import AuthService from '@/services/AuthService'
import { authHeaders, tokenLooksValid } from '@/utils/session'

/* Point this at your API root */
const apiClient = axios.create({
  baseURL: 'https://dashamajig.chibitronics.com/api',
  withCredentials: false,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  }
})

let sessionRefreshPromise = null

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
    config.headers['X-Auth-Token'] = token
  }
  return config
})

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { config, response } = error
    if (response?.status !== 401 || !config || config._authRetried) {
      return Promise.reject(error)
    }

    config._authRetried = true
    if (!sessionRefreshPromise) {
      sessionRefreshPromise = AuthService.claimGoogleSession()
        .finally(() => { sessionRefreshPromise = null })
    }

    try {
      const { data, status } = await sessionRefreshPromise
      if (status === 200 && data?.token && tokenLooksValid(data.token)) {
        localStorage.setItem('token', data.token)
        if (data.username) localStorage.setItem('username', data.username)
        config.headers.Authorization = `Bearer ${data.token}`
        config.headers['X-Auth-Token'] = data.token
        return apiClient(config)
      }
    } catch {
      /* no Google handoff available */
    }

    window.dispatchEvent(new Event('dashamajig:session-expired'))
    return Promise.reject(error)
  }
)

export default {
  /* ─── Pagination / filter aware GET ─────────────────────────────
   * params = { page, per_page, q, tag }
   * API response shape:
   *   { page, per_page, total, customers:[ … ] }
   * This helper simply forwards params and returns the whole payload.
   */
  list(params = {}) {
    return apiClient.get('/customers', { params })
  },

  // ---------- Existing helpers (unchanged) ----------
  getCustomers() {                    // still used somewhere else?
    return apiClient.get('/customers')
  },

  getCustomer(id) {
    return apiClient.get(`/customers/${id}`, { headers: authHeaders() })
  },
  getById(id) {
    return this.getCustomer(id)
  },

  updateCustomer(id, payload) {
    return apiClient.patch(`/customers/${id}`, payload, { headers: authHeaders() })
  },
  update(id, payload) {
    return this.updateCustomer(id, payload)
  },

  /** Merge another customer into `keepId` (+keepId+ survives). */
  merge(keepId, mergeCustomerId) {
    return apiClient.post(
      `/customers/${keepId}/merge`,
      { merge_customer_id: mergeCustomerId },
      { headers: authHeaders() }
    )
  },

  lastEmail(id, { shallow = false } = {}) {
    return apiClient.get(`/customers/${id}/last_email`, {
      params: shallow ? { shallow: true } : undefined,
      headers: authHeaders()
    })
  },

  importSentEmails(id, max = 50) {
    return apiClient.post(
      `/customers/${id}/import_sent_emails`,
      { max },
      { headers: authHeaders() }
    )
  },

  undoImportSentEmails(id, importBatchId) {
    return apiClient.post(
      `/customers/${id}/undo_import_sent_emails`,
      { import_batch_id: importBatchId },
      { headers: authHeaders() }
    )
  },

  getOrders(id) {
    return apiClient.get(`/customers/${id}/orders`)
  },

  updateNote(customerId, noteId, body) {
    return apiClient.patch(
      `/customers/${customerId}/notes/${noteId}`,
      { body },
      { headers: authHeaders() }
    )
  },

  deleteNote(customerId, noteId) {
    return apiClient.delete(`/customers/${customerId}/notes/${noteId}`, {
      headers: authHeaders()
    })
  },

  listCompanies(params = {}) {
    return apiClient.get('/companies', { params })
  },

  toggleCompanyStar(companyName) {
    return apiClient.post('/companies/star', { company: companyName })
  },

  updateCompanyProfile(companyName, payload) {
    return apiClient.patch('/companies/profile', { company: companyName, ...payload })
  },

  createCompany(payload) {
    return apiClient.post('/companies', payload)
  },

  addCompanyContacts(companyName, customerIds) {
    return apiClient.post('/companies/contacts', {
      company: companyName,
      customer_ids: customerIds
    })
  },

  /* ─── Create new customer (modal) ──────────────────────
   * validateStatus lets us catch 409 Conflict on duplicates
   */
  create(payload) {
    return apiClient.post('/customers', payload, {
      validateStatus: () => true   // we’ll inspect status in the modal
    })
  },

  previewImport(file) {
    const form = new FormData()
    form.append('file', file)
    return apiClient.post('/customers/import/preview', form, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },

  importCsv(file) {
    const form = new FormData()
    form.append('file', file)
    return apiClient.post('/customers/import', form, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },

  bulkApplyTags(payload) {
    return apiClient.post('/customers/tags/bulk', payload)
  },

  bulkRemoveTags(payload) {
    return apiClient.post('/customers/tags/bulk_remove', payload)
  },

  toggleStar(id) {
    return apiClient.post(`/customers/${id}/star`, {}, { headers: authHeaders() })
  },

  getCompany(companyName) {
    return apiClient.get('/companies/show', { params: { company: companyName } })
  },

  updateCompanyContactStatus(companyName, customerId, companyStatus) {
    return apiClient.patch('/companies/contacts/status', {
      company: companyName,
      customer_id: customerId,
      company_status: companyStatus
    })
  }
}
